import type { ThemePalette } from "@baton/api";
import { initialsOf, useTheme } from "@baton/ui";
import type { CSSProperties } from "react";

/**
 * The team identity tile: a team's initials on the team's own colour.
 *
 * 2026-09-18, by the owner's question ("shouldn't the accent colour change it up here too?"),
 * revising 024's D-2404 and the fixed `--color-workspace-mark` of
 * `specs/015-web-frontend/contracts/design-system.md` § Chrome and work-surface tokens: every tile
 * drew the same Workspace Amber, so in a switcher listing several teams the tile identified none of
 * them. It is drawn from that team's served palette instead, which the team list carries per row
 * (`applied_palettes` batches them, 024 FR-024-028), in the accent solid with the `on_solid` the
 * generator proved against it, so the initials pass their contrast wherever the tile sits.
 *
 * This is not the palette mechanism of 024 FR-024-026 and breaks no chrome rule: no token is
 * rewritten and the tile is not a chrome colour. It reads one team's own data the way an `Avatar`
 * reads one actor's hashed hue, which is why a row whose palette has not loaded yet, the
 * memberships of `GET /me` before the team list answers, keeps the fixed token as its fallback.
 */
export interface TeamMarkProps {
  readonly name: string;
  /** The team's applied palette; absent for a row read from the memberships before the list answers. */
  readonly theme?: ThemePalette | undefined;
  /** The team's uploaded logo (001 FR-068), which is drawn in place of the initials when there is one. */
  readonly logoUrl?: string | null | undefined;
  readonly className: string;
}

export function TeamMark({ name, theme, logoUrl, className }: TeamMarkProps) {
  const { resolvedTheme } = useTheme();
  const ramp = theme?.[resolvedTheme];
  const style = ramp === undefined ? undefined : ({ background: ramp.accent.solid, color: ramp.on_solid } as CSSProperties);
  if (logoUrl !== undefined && logoUrl !== null) {
    return (
      <span className={className} data-logo="true" aria-hidden="true">
        <img src={logoUrl} alt="" className="b-team-mark__image" />
      </span>
    );
  }
  return (
    <span className={className} style={style} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}
